import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import LayoutSubmaster from "../../components/layoutSubmasterConfig/layoutSubmaster";
import { apiClientSubmaster } from "../../services/setupClientSubmaster/apiClientSubmaster";
import { toast } from "react-toastify";
import LocalStorage from "../../helpers/localstorage";
import {
  downloadBlob,
  formatDocumentDateForFileName,
  formatDateForFileName,
  runPdfOnlyBackup,
  sanitizeFileName,
  sanitizeZipPathSegment,
} from "../../helpers/backupExport";
import { formatDeliveryReportLabel } from "../../helpers/deliveryReport";
import { groupIndividualDocuments } from "../../helpers/individualDocumentGroups";
import "bootstrap-icons/font/bootstrap-icons.css";

interface Employee {
  id: number;
  name: string;
  cpf: string;
}

interface CompanySnapshot {
  id?: number;
  name_company?: string;
  cnpj?: string;
  image?: string;
}

interface DataRow {
  id: number;
  subMainImageUrl: string;
  submasterId: number;
  epirecordId: number;
  createdAt: string;
  updatedAt: string;
  employeesSubmasterId: number;
  receivedsignature: string;
  receivedbiometric: string;
  pdfdocumentregistered: string | null;
  delivery_type?: string | null;
  delivery_reason?: string | null;
  name_usuario?: string | null;
  submasterCompanyId?: number | null;
  documentRecordIds: number[];
  epiCount: number;
  employeesSubmaster: Employee;
  submaster_company?: CompanySnapshot | null;
}

interface ConfigDocumentEmployeePageProps {
  photoByEpi?: boolean;
}

const formatCpf = (cpf: string) => {
  const numbers = cpf.replace(/\D/g, "");
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const getLocalDateFilterValue = (date: string) => {
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const sortDocumentsAlphabetically = (rows: DataRow[]) =>
  [...rows].sort((firstDocument, secondDocument) =>
    firstDocument.employeesSubmaster.name.localeCompare(
      secondDocument.employeesSubmaster.name,
      "pt-BR",
      {
        sensitivity: "base",
        ignorePunctuation: true,
      }
    )
  );

const buildEmployeeFolderName = (employee: Employee) =>
  sanitizeZipPathSegment(`${employee.name} - CPF ${formatCpf(employee.cpf)}`);

const buildCompanyFolderName = (
  companyName: string | null | undefined,
  fallbackCompanyName: string
) => sanitizeZipPathSegment(companyName || fallbackCompanyName || "Empresa");

function ConfigDocumentEmployeePage({ photoByEpi = false }: ConfigDocumentEmployeePageProps) {
  const storage = useMemo(() => new LocalStorage(), []);
  const authToken = useMemo(() => String(storage.getAuthToken() || ""), [storage]);
  const submasterId = useMemo(() => Number(storage.getId()), [storage]);
  const baseCompanyName = useMemo(() => storage.getName() || "Empresa", [storage]);
  const apiClient = useMemo(() => apiClientSubmaster(authToken), [authToken]);

  const [data, setData] = useState<DataRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    cpf: "",
    startDate: "",
    endDate: "",
  });
  const [invalidDateFilters, setInvalidDateFilters] = useState({
    startDate: false,
    endDate: false,
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFailures, setBackupFailures] = useState<
    { label: string; reason: string }[]
  >([]);
  const [deletePermissionEnabled, setDeletePermissionEnabled] = useState(true);
  const [deliveryTypeReasonEnabled, setDeliveryTypeReasonEnabled] = useState(false);
  const [fallbackCompanyName, setFallbackCompanyName] = useState(baseCompanyName);
  const [photoReportAllowed, setPhotoReportAllowed] = useState(false);

  const fetchPhotoReportPdf = async (row: DataRow): Promise<Blob> => {
    const response = await apiClient.post(
      "/exportIndividualPhotoPdf",
      { documentRecordIds: row.documentRecordIds },
      { responseType: "blob" }
    );

    return new Blob([response.data], { type: "application/pdf" });
  };

  const handleDownload = async (row: DataRow) => {
    if (photoByEpi) {
      try {
        const blob = await fetchPhotoReportPdf(row);
        const fileName = `${sanitizeFileName(
          `relatorio_individual_foto_por_epi_${formatDocumentDateForFileName(
            row.createdAt
          )}_${row.id}`
        )}.pdf`;
        downloadBlob(blob, fileName);
      } catch (error) {
        console.error("Erro ao baixar o relatório individual com foto por EPI:", error);
        toast.error("Não foi possível gerar o relatório com foto por EPI.");
      }
      return;
    }

    if (!row.pdfdocumentregistered) {
      toast.info("Nenhum PDF registrado para este item.");
      return;
    }

    const url = `https://alexios.com.br:9597/uploads/signature/${row.pdfdocumentregistered}`;
    window.open(url, "_blank");
  };

  const handlePreview = async (row: DataRow) => {
    if (photoByEpi) {
      try {
        const blob = await fetchPhotoReportPdf(row);
        setPreviewUrl((previous) => {
          if (previous?.startsWith("blob:")) window.URL.revokeObjectURL(previous);
          return window.URL.createObjectURL(blob);
        });
      } catch (error) {
        console.error("Erro ao visualizar o relatório individual com foto por EPI:", error);
        toast.error("Não foi possível gerar o relatório com foto por EPI.");
      }
      return;
    }

    if (!row.pdfdocumentregistered) {
      toast.info("Nenhum PDF registrado para este item.");
      return;
    }

    const url = `https://alexios.com.br:9597/uploads/signature/${row.pdfdocumentregistered}`;
    setPreviewUrl(url);
  };

  const fetchRegisteredDocumentPdf = async (fileName: string): Promise<Blob> => {
    const response = await apiClient.post(
      `/downloadRegisteredDocumentPdf`,
      { fileName },
      { responseType: "blob" }
    );

    return new Blob([response.data], { type: "application/pdf" });
  };

  const handleBackup = async () => {
    if (backupLoading) return;

    if (filteredData.length === 0) {
      toast.info("Não há registros para incluir no backup.");
      return;
    }

    setBackupLoading(true);
    setBackupFailures([]);
    const dateSuffix = formatDateForFileName(new Date());
    const toastId = toast.info(`Gerando backup... 0/${filteredData.length}`, {
      position: "top-right",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    });

    try {
      const summary = await runPdfOnlyBackup<DataRow>({
        items: filteredData,
        zipRootName: `backup_relatorios_${dateSuffix}`,
        concurrency: 3,
        getLabel: (row) => row.employeesSubmaster.name,
        onProgress: (done, total) => {
          toast.update(toastId, { render: `Gerando backup... ${done}/${total}` });
        },
        processItem: async (row) => {
          const folderPath = [
            buildCompanyFolderName(row.submaster_company?.name_company, fallbackCompanyName),
            buildEmployeeFolderName(row.employeesSubmaster),
          ];

          if (!photoByEpi && !row.pdfdocumentregistered) {
            return { pdf: null, folderPath };
          }

          const blob = photoByEpi
            ? await fetchPhotoReportPdf(row)
            : await fetchRegisteredDocumentPdf(row.pdfdocumentregistered as string);
          const fileName = `${sanitizeFileName(
            photoByEpi
              ? `relatorio_individual_foto_por_epi_${formatDocumentDateForFileName(row.createdAt)}_${row.id}`
              : `comprovante_recebimento_epi_${formatDocumentDateForFileName(row.createdAt)}_${row.id}`
          )}.pdf`;

          return { pdf: { blob, fileName }, folderPath };
        },
      });

      downloadBlob(summary.blob, summary.zipFileName);

      const hasFailures = summary.failedItems.length > 0;
      toast.update(toastId, {
        render: hasFailures
          ? `Backup concluído com ${summary.failedItems.length} falha(s). ${summary.pdfCount} PDFs incluídos.`
          : `Backup concluído! ${summary.pdfCount} PDFs incluídos, ${summary.skippedCount} sem PDF disponível.`,
        type: hasFailures ? "warning" : "success",
        isLoading: false,
        autoClose: 5000,
      });

      if (hasFailures) {
        setBackupFailures(summary.failedItems);
      }
    } catch (error) {
      console.error("Erro ao gerar backup:", error);
      toast.update(toastId, {
        render: "Erro ao gerar o backup. Tente novamente.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDeleteDocument = async (row: DataRow) => {
    if (!deletePermissionEnabled) {
      toast.warning("Ative a permissão de exclusão para remover este registro.");
      return;
    }

    const confirmed = window.confirm(
      `Atenção: todos os dados deste documento serão apagados permanentemente.

Funcionário: ${row.employeesSubmaster.name}
Data de retirada: ${formatDate(
        row.createdAt
      )}\n\nDeseja continuar?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const recordIds = row.documentRecordIds?.length
        ? row.documentRecordIds
        : [row.id];
      const responses = await Promise.all(
        recordIds.map((recordId) =>
          apiClient.post(
            `/crud/delete/EpicontrolEmployeesSubmaster/${recordId}/${submasterId}`
          )
        )
      );

      const failedResponse = responses.find((response) => !response?.data?.res);
      if (failedResponse) {
        throw new Error(
          failedResponse?.data?.message || "Não foi possível excluir o registro."
        );
      }

      setData((previous) => previous.filter((item) => item.id !== row.id));
      toast.success("Documento excluído com sucesso.");
    } catch (error) {
      console.error("Erro ao excluir documento:", error);
      toast.error("Erro ao excluir o documento.");
    }
  };

  useEffect(() => {
    const loadDocuments = async () => {
      setLoading(true);
      try {
        const [documentsResponse, submasterResponse, permissionsResponse] = await Promise.all([
          apiClient.post(`/crud/getDocuments/EpicontrolEmployeesSubmaster/${submasterId}`),
          apiClient.post(`/crud/getdata/Submaster/${submasterId}/true`),
          apiClient.post("/reportPermissions", {}),
        ]);

        const submasterData = Array.isArray(submasterResponse.data)
          ? submasterResponse.data[0]
          : submasterResponse.data;

        setDeletePermissionEnabled(submasterData?.delete_permission_enabled !== false);
        setDeliveryTypeReasonEnabled(
          permissionsResponse?.data?.delivery_type_reason_permission_enabled === true
        );
        setPhotoReportAllowed(
          permissionsResponse?.data?.report_individual_photo_by_epi_permission_enabled === true
        );
        setFallbackCompanyName(
            submasterData?.fantasy_name ||
            submasterData?.name ||
            baseCompanyName ||
            "Empresa"
        );
        const groupedDocuments = groupIndividualDocuments(
          documentsResponse.data as Omit<DataRow, "documentRecordIds" | "epiCount">[]
        );
        setData(sortDocumentsAlphabetically(groupedDocuments as DataRow[]));
      } catch (error) {
        console.error("Erro ao buscar os documentos:", error);
        toast.error("Erro ao buscar os documentos.");
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [apiClient, baseCompanyName, submasterId]);

  useEffect(
    () => () => {
      if (previewUrl?.startsWith("blob:")) {
        window.URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, validity } = event.currentTarget;

    if (name === "startDate" || name === "endDate") {
      setInvalidDateFilters((previous) => ({
        ...previous,
        [name]: validity.badInput || validity.typeMismatch,
      }));
    }

    setFilters((previous) => ({ ...previous, [name]: value }));
    setResetPaginationToggle((previous) => !previous);
  };

  const handleDateInput = (event: FormEvent<HTMLInputElement>) => {
    const { name, validity } = event.currentTarget;

    if (name === "startDate" || name === "endDate") {
      setInvalidDateFilters((previous) => ({
        ...previous,
        [name]: validity.badInput || validity.typeMismatch,
      }));
    }
  };

  const clearFilters = () => {
    setFilters({ name: "", cpf: "", startDate: "", endDate: "" });
    setInvalidDateFilters({ startDate: false, endDate: false });
    setResetPaginationToggle((previous) => !previous);
  };

  const hasInvalidDateRange =
    invalidDateFilters.startDate ||
    invalidDateFilters.endDate ||
    (!!filters.startDate &&
      !!filters.endDate &&
      filters.startDate > filters.endDate);

  const filteredData = useMemo(
    () => {
      if (hasInvalidDateRange || (photoByEpi && !photoReportAllowed)) return [];

      return data.filter((item) => {
        const nameMatch = item.employeesSubmaster.name
          .toLowerCase()
          .includes(filters.name.toLowerCase());
        const cpfMatch = formatCpf(item.employeesSubmaster.cpf).includes(filters.cpf);
        // Use o mesmo dia local exibido na tabela. Comparar diretamente a parte
        // UTC do ISO pode incluir uma retirada do dia anterior no horário de Brasília.
        const documentDate = getLocalDateFilterValue(item.createdAt);
        const startDateMatch =
          !filters.startDate || (!!documentDate && documentDate >= filters.startDate);
        const endDateMatch =
          !filters.endDate || (!!documentDate && documentDate <= filters.endDate);
        return nameMatch && cpfMatch && startDateMatch && endDateMatch;
      });
    },
    [data, filters, hasInvalidDateRange, photoByEpi, photoReportAllowed]
  );

  const isReportAvailable = (row: DataRow) =>
    photoByEpi
      ? photoReportAllowed && row.documentRecordIds.length > 0
      : !!row.pdfdocumentregistered;
  const availablePdfs = filteredData.filter(isReportAvailable).length;
  const pendingPdfs = filteredData.length - availablePdfs;

  const columns: TableColumn<DataRow>[] = [
    ...(deliveryTypeReasonEnabled ? [{
      name: "Tipo / motivo",
      cell: (row) => (
        <span>{formatDeliveryReportLabel(row.delivery_type, row.delivery_reason)}</span>
      ),
      grow: 1.2,
    } as TableColumn<DataRow>] : []),
    {
      name: "Funcionário",
      selector: (row) => row.employeesSubmaster.name,
      sortable: true,
      grow: 1.4,
      cell: (row) => (
        <div className="report-person">
          <div className="report-person__avatar">
            {row.employeesSubmaster.name.charAt(0).toUpperCase()}
          </div>
          <div className="report-person__meta">
            <span className="report-person__name">{row.employeesSubmaster.name}</span>
            <small>
              {photoByEpi ? "Foto por EPI" : "Documento individual"}
              {row.epiCount > 1 ? ` • ${row.epiCount} EPIs` : ""}
            </small>
          </div>
        </div>
      ),
    },
    {
      name: "CPF",
      selector: (row) => row.employeesSubmaster.cpf,
      sortable: true,
      cell: (row) => (
        <span className="pill-badge report-pill-neutral">
          {formatCpf(row.employeesSubmaster.cpf)}
        </span>
      ),
    },
    {
      name: "Data de retirada",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <div className="report-date">
          <i className="bi bi-calendar-check" />
          <span>{formatDate(row.createdAt)}</span>
        </div>
      ),
      grow: 1.05,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`pill-badge ${
            isReportAvailable(row) ? "report-pill-success" : "report-pill-warning"
          }`}
        >
          <i
            className={`bi ${
              isReportAvailable(row) ? "bi-check2-circle" : "bi-hourglass-split"
            } me-1`}
          />
          {isReportAvailable(row) ? "PDF disponível" : "Pendente"}
        </span>
      ),
      grow: 0.95,
    },
    {
      name: "PDF",
      cell: (row) => (
        <div className="report-actions">
          <button
            className={`btn btn-sm report-action-btn ${
              isReportAvailable(row) ? "btn-outline-primary" : "btn-outline-secondary"
            }`}
            onClick={() => handlePreview(row)}
            disabled={!isReportAvailable(row)}
            title={isReportAvailable(row) ? "Pré-visualizar PDF" : "Nenhum PDF disponível"}
          >
            <i className="bi bi-eye" />
          </button>
          <button
            className={`btn btn-sm report-action-btn ${
              isReportAvailable(row) ? "btn-outline-danger" : "btn-outline-secondary"
            }`}
            onClick={() => handleDownload(row)}
            disabled={!isReportAvailable(row)}
            title={isReportAvailable(row) ? "Baixar PDF" : "Nenhum PDF disponível"}
          >
            <i className="bi bi-file-earmark-pdf" />
          </button>
          {deletePermissionEnabled && (
          <button
            className="btn btn-sm report-action-btn btn-outline-danger"
            onClick={() => handleDeleteDocument(row)}
            title={
              deletePermissionEnabled
                ? "Excluir documento"
                : "Ative a permissão para excluir"
            }
          >
            <i className="bi bi-trash" />
          </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      grow: 0.8,
    },
  ];

  const noDataComponent = (
    <div className="report-empty-state">
      <div className="report-empty-state__icon">
        <i className="bi bi-file-earmark-text" />
      </div>
      <h4>Nenhum documento encontrado</h4>
      <p>
        {photoByEpi
          ? "Use os filtros acima para localizar um Relatório Individual com Foto por EPI."
          : "Use os filtros acima para localizar um funcionário com documento individual."}
      </p>
      <button type="button" className="btn btn-outline-primary" onClick={clearFilters}>
        Limpar filtros
      </button>
    </div>
  );

  return (
    <LayoutSubmaster>
      <div className="report-config-page individual-document-page">
        <section className="dashboard-hero report-hero mb-4">
          <div className="report-hero__content">
            <span className="pill-badge">Documentos</span>
            <h1 className="dashboard-title">
              {photoByEpi
                ? "Relatório Individual com Foto por EPI"
                : "Relatório individual"}
            </h1>
            <p className="dashboard-subtitle">
              {photoByEpi
                ? "Relatório Individual com Foto por EPI. Cada linha apresenta a foto da retirada depois do CA e antes da assinatura."
                : "Consulte os documentos individuais emitidos para os funcionários e visualize o PDF quando estiver disponível."}
            </p>
          </div>
          <div className="report-hero__badge">
            <i className="bi bi-file-earmark-person" />
            <div>
              <span>{photoByEpi ? "Foto por EPI" : "Controle individual"}</span>
              <strong>{filteredData.length} registros ativos</strong>
            </div>
          </div>
        </section>

        {photoByEpi && !loading && !photoReportAllowed && (
          <div className="alert alert-warning d-flex align-items-center gap-2" role="alert">
            <i className="bi bi-lock" />
            <span>
              O Relatório Individual com Foto por EPI não está liberado para esta empresa.
            </span>
          </div>
        )}

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-people fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Registros</p>
                <h2 className="metric-card__value">{data.length}</h2>
                <p className="metric-card__caption">
                  {photoByEpi
                    ? "Entregas listadas para o relatório com foto por EPI"
                    : "Funcionários listados com documento individual"}
                </p>
              </div>
            </article>
          </div>

          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-file-earmark-pdf fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">PDFs disponíveis</p>
                <h2 className="metric-card__value">{availablePdfs}</h2>
                <p className="metric-card__caption">Prontos para visualização ou download</p>
              </div>
            </article>
          </div>

          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-hourglass-split fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Pendentes</p>
                <h2 className="metric-card__value">{pendingPdfs}</h2>
                <p className="metric-card__caption">Itens sem PDF registrado na consulta atual</p>
              </div>
            </article>
          </div>
        </div>

        <section className="section-panel report-filter-panel mb-4">
          <div className="section-heading">
            <div>
              <h3>Filtros da consulta</h3>
              <p>Busque o documento por funcionário, CPF ou período de retirada.</p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary report-clear-btn"
              onClick={clearFilters}
            >
              <i className="bi bi-arrow-counterclockwise me-1" />
              Limpar pesquisa
            </button>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-lg-3 col-md-6">
              <label className="form-label">Nome do funcionário</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-person text-primary" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">CPF</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-card-text text-primary" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="000.000.000-00"
                  name="cpf"
                  value={filters.cpf}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">Data inicial</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-calendar3 text-primary" />
                </span>
                <input
                  type="date"
                  className={`form-control ${
                    invalidDateFilters.startDate ? "is-invalid" : ""
                  }`}
                  name="startDate"
                  value={filters.startDate}
                  max={filters.endDate || undefined}
                  onChange={handleFilterChange}
                  onInput={handleDateInput}
                  aria-invalid={invalidDateFilters.startDate}
                />
              </div>
              {invalidDateFilters.startDate && (
                <div className="invalid-feedback d-block">Informe uma data válida.</div>
              )}
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">Data final</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-calendar3 text-primary" />
                </span>
                <input
                  type="date"
                  className={`form-control ${
                    invalidDateFilters.endDate ? "is-invalid" : ""
                  }`}
                  name="endDate"
                  value={filters.endDate}
                  min={filters.startDate || undefined}
                  onChange={handleFilterChange}
                  onInput={handleDateInput}
                  aria-invalid={invalidDateFilters.endDate}
                />
              </div>
              {invalidDateFilters.endDate && (
                <div className="invalid-feedback d-block">Informe uma data válida.</div>
              )}
            </div>

          </div>
        </section>

        <section className="section-panel report-table-panel">
          <div className="section-heading">
            <div>
              <h3>Documentos emitidos</h3>
              <p>
                {photoByEpi
                  ? "Visualize ou baixe o relatório com uma foto em cada linha de EPI."
                  : "Acesse rapidamente a visualização e o download dos PDFs individuais."}
              </p>
            </div>
            <div className="report-table-panel__summary">
              <span className="pill-badge report-pill-success">{availablePdfs} disponíveis</span>
              <span className="pill-badge report-pill-warning">{pendingPdfs} pendentes</span>
              <button
                type="button"
                className="btn btn-sm btn-primary report-backup-btn"
                onClick={handleBackup}
                disabled={backupLoading || filteredData.length === 0}
              >
                {backupLoading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                ) : (
                  <i className="bi bi-cloud-download me-1" />
                )}
                Fazer Backup em PDF
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            pagination
            paginationResetDefaultPage={resetPaginationToggle}
            highlightOnHover
            pointerOnHover
            responsive
            noHeader
            noDataComponent={noDataComponent}
            paginationComponentOptions={{
              rowsPerPageText: "Linhas",
              rangeSeparatorText: "de",
              selectAllRowsItem: true,
              selectAllRowsItemText: "Todos",
            }}
          />
        </section>
      </div>

      {previewUrl && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setPreviewUrl(null)} />
          <div className="modal fade show d-block" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ background: "linear-gradient(135deg, #0d6efd, #4f8cff)" }}
                >
                  <div>
                    <h5 className="modal-title text-white mb-1">
                      <i className="bi bi-eye me-2" />
                      {photoByEpi
                        ? "Relatório Individual com Foto por EPI"
                        : "Pré-visualizar PDF"}
                    </h5>
                    <small className="text-white-50">
                      {photoByEpi
                        ? "Confira as fotos associadas a cada EPI antes de baixar o arquivo."
                        : "Confira o documento individual antes de abrir o arquivo."}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="mt-4 mr-4 btn-close btn-close-white"
                    onClick={() => setPreviewUrl(null)}
                  />
                </div>
                <div className="modal-body p-0 report-preview-modal__body">
                  <iframe
                    src={previewUrl}
                    title={
                      photoByEpi
                        ? "Pré-visualização do Relatório Individual com Foto por EPI"
                        : "Pré-visualização do PDF individual"
                    }
                    className="w-100 h-100 border-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {backupFailures.length > 0 && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setBackupFailures([])} />
          <div className="modal fade show d-block" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle text-warning me-2" />
                    Itens que falharam no backup
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setBackupFailures([])}
                  />
                </div>
                <div className="modal-body">
                  <p>Os demais itens foram incluídos normalmente no arquivo baixado.</p>
                  <ul className="list-group">
                    {backupFailures.map((failure, index) => (
                      <li key={index} className="list-group-item">
                        <strong>{failure.label}</strong>: {failure.reason}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setBackupFailures([])}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </LayoutSubmaster>
  );
}

export default ConfigDocumentEmployeePage;
